/**
 * Setup › Price limits (CU-869fkbjdh, UX rethink design (c)).
 *
 * One table where every column means one thing — Where · When · Floor ·
 * Ceiling — over the same `repricing_price_limit` rows as before: the
 * four columns are a PROJECTION of `matrix_id`, `protects_dates`, `name`,
 * the dates, the scope keys and min/max (`@services/priceLimitsProjection`).
 * Rows come "as the engine reads them": protections first, then ordinary
 * rules by the server's `specificity` (`@services/priceLimitsOrder`, M7 —
 * nothing is recomputed here). An amount is edited in the cell and
 * travels as the whole row through `useSetupCollection` (diff → PUT, M5);
 * the structure is edited in a drawer that saves nothing until Save,
 * through a direct call the collection then *adopts* — so "Add rule"
 * creates no row and no 20 €/day phantom floor (criterion 9). Delete is a
 * direct call the collection *forgets*. "What applies here?" is a dialog
 * behind "Test a cell" in the toolbar, answered by the engine
 * (`POST /setup/price-limits/resolve`); the rows it used light up here
 * until the next Check or a filter change.
 *
 * Gone, on purpose (rethink § 3.8): the three group headers, the shield,
 * the MATRIX chip, the date-mode switch inside a cell, the specificity
 * separator and the local `specificity()`, the merged binding cell, "Add
 * limit" with its 20 € row, "Add matrix", the clone row, the two doctrine
 * paragraphs (behind the ⓘ now), the "Any pricing" facet.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
	Box, Button, IconButton, Link, MenuItem, Pagination, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
	TextField, Typography,
} from '@mui/material'
import {
	AppCardTitle, AppFilterBar, AppFilterChip, AppInfoButton, AppLegendChips, AppNote, AppTableSkeleton, AppToolbarCreateButton,
	secondaryButtonSx,
} from '@components'
import { dsColorNew } from '@palette'
import { IconSeeker } from '@ui-seeker/core'
import useToast from '@hooks/useToast'
import {
	DeleteOutline as DeleteIcon, ContentCopyOutlined as CopyIcon, EditOutlined as EditIcon,
	UploadFileOutlined as UploadIcon, DownloadOutlined as DownloadIcon, SearchOutlined as SearchIcon,
} from 'Pages/DsRepricing/@components/icons'
import DateRangeField from 'Pages/DsRepricing/@components/DateRangeField'
import { futurePresets } from 'Pages/DsRepricing/@services/dateRange'
import { useHighlight, highlightSx } from 'Pages/DsRepricing/Setup/@services/useHighlight'
import { useSetupCollection, setupApi, idKey, prepaidKey } from 'Pages/DsRepricing/Setup/@services'
import { describeApiError } from 'Pages/DsRepricing/Setup/@services/useSetupCollection'
import { priceMatrices as matrixAdapter } from 'Pages/DsRepricing/Setup/@services/adapters'
import { Surface, EmptyState, HelpNote } from 'Pages/DsRepricing/Setup/@components/Surface'
import { rowHoverSx, cellPadSx } from 'Pages/DsRepricing/Setup/@components/formStyles'
import UnsavedChip from 'Pages/DsRepricing/Setup/@components/UnsavedChip'
import { uid } from 'Pages/DsRepricing/Setup/@mocks'
import { engineOrder, applyFilters, sortRows, countsByKind, EMPTY_FILTERS, KIND_CHIPS } from 'Pages/DsRepricing/Setup/@services/priceLimitsOrder'
import { readsAs, fmtMoney } from 'Pages/DsRepricing/Setup/@services/priceLimitsProjection'

import CsvImportDialog, { downloadCsv } from './CsvImportDialog'
import { CoverageGapWarning, enumerateDays, ruleCoversDay } from './coverage'
import ExplainDialog from './ExplainDialog'
import RuleDrawer from './RuleDrawer'
import { CompactSelect } from './pickers'
import { useRuleLabels, HeadCell, WhereCell, WhenCell, FloorCell, CeilingCell } from './cells'

// Persistence: @services/useSetupCollection (entity 'priceLimits').
const PAGE_SIZE = 25
// The mock v2 measured 7 px of row padding; `cellPadSx` (shared by the five tabs) stays at 10.
const cellSx = { ...cellPadSx, py: 0.875 }

/** Mock rows behind the `USE_MOCKS` flag — the same five as before. */
const DEFAULT_LIMITS = [
	{ id: uid(), scope_ps_provider_id: null, scope_is_prepaid: null, scope_ps_insurance_id: null,
	  scope_office_group: null, scope_offices: null, scope_categories: null, scope_duration: null,
	  date_from: null, date_to: null, min_price: 18, max_price: null },
	{ id: uid(), scope_ps_provider_id: null, scope_is_prepaid: null, scope_ps_insurance_id: null,
	  scope_office_group: 'airports', scope_offices: null, scope_categories: [{ type: 'macro', code: 'mini' }],
	  scope_duration: { unit: 'days', min: 1, max: 3 }, date_from: null, date_to: null, min_price: 22, max_price: null },
	{ id: uid(), scope_ps_provider_id: null, scope_is_prepaid: true, scope_ps_insurance_id: null,
	  scope_office_group: null, scope_offices: ['LPA-AP'], scope_categories: [{ type: 'sipp', code: 'CDMR' }],
	  scope_duration: { unit: 'days', min: 7, max: 14 }, date_from: '2026-12-20', date_to: '2027-01-05', min_price: 45, max_price: null },
	{ id: uid(), scope_ps_provider_id: null, scope_is_prepaid: null, scope_ps_insurance_id: null,
	  scope_office_group: null, scope_offices: ['TFS-AP'], scope_categories: [{ type: 'sipp', code: 'FVAR' }],
	  scope_duration: null, date_from: null, date_to: null, min_price: 55, max_price: 200 },
	// A date protection: any rental of up to 30 days that crosses
	// 26–31 Dec is floored at 50, whatever its pickup date.
	{ id: uid(), scope_ps_provider_id: null, scope_is_prepaid: null, scope_ps_insurance_id: null,
	  scope_office_group: null, scope_offices: null, scope_categories: null, scope_duration: null,
	  date_from: '2026-12-26', date_to: '2026-12-31', protects_dates: true, name: 'Christmas', min_price: 50, max_price: null },
]

/** v3/v4 mock rows → the current shape (unchanged). */
function migrateLimit(r) {
	if (!r || typeof r !== 'object') return r
	let scope_categories = r.scope_categories
	if (scope_categories === undefined) {
		const list = []
		if (r.scope_macro) list.push({ type: 'macro', code: r.scope_macro })
		if (r.scope_sipp)  list.push({ type: 'sipp',  code: r.scope_sipp })
		scope_categories = list.length ? list : null
	}
	let scope_duration = r.scope_duration
	if (scope_duration === undefined) {
		const b = r.scope_duration_bucket
		const m = b ? /^(\d+)(?:-(\d+)|(\+))?$/.exec(b) : null
		scope_duration = m ? { unit: 'days', min: Number(m[1]), max: m[2] ? Number(m[2]) : null } : null
	}
	const { scope_macro, scope_sipp, scope_duration_bucket, scope_brand, scope_product,
	        scope_franchise, scope_tariff_type, scope_insurance_type, scope_office, ...rest } = r
	return {
		...rest,
		scope_ps_provider_id:  r.scope_ps_provider_id  ?? null,
		scope_is_prepaid:      r.scope_is_prepaid      ?? null,
		scope_ps_insurance_id: r.scope_ps_insurance_id ?? null,
		scope_offices: r.scope_offices ?? (scope_office ? [scope_office] : null),
		scope_categories, scope_duration,
	}
}

const SORT_LABEL = { where: 'Where', when: 'When', floor: 'Floor', ceiling: 'Ceiling' }

export default function PriceLimitsTab() {
	const { showToast } = useToast()
	const catalog = useRuleLabels()
	// The real rows of the tenant, synced per row as always.
	const [limits, setLimits, sync] = useSetupCollection('priceLimits', { mockDefault: DEFAULT_LIMITS, normalise: migrateLimit })

	// The matrices this tenant has, for the bindings to point at and the
	// drawer to preview. Read-only here: they are authored in their own tab.
	const [matrices, setMatrices] = useState([])
	useEffect(() => {
		let alive = true
		setupApi.priceMatrices.list()
			.then(items => { if (alive) setMatrices((items || []).map(matrixAdapter.fromServer)) })
			.catch(() => { if (alive) setMatrices([]) })
		return () => { alive = false }
	}, [])
	const matrixById = useMemo(() => new Map(matrices.map(m => [String(m.id), m])), [matrices])

	const [filters, setFilters] = useState(EMPTY_FILTERS)
	const [sort, setSort] = useState(null)            // null = as the engine reads them
	const [page, setPage] = useState(1)
	const [csvOpen, setCsvOpen] = useState(false)
	const [explainOpen, setExplainOpen] = useState(false)
	const [drawer, setDrawer] = useState(null)        // { mode, initial }
	const [verdict, setVerdict] = useState(null)      // from "What applies here?" — lights the rows; a filter change lets them go
	const [jumpId, setJumpId] = useState(null)        // the page follows this row once (a verdict's first used rule)

	// ── Row flash: after a Save, and for the Activity deep link.
	const [flashId, setFlashId] = useState(null)
	useEffect(() => {
		if (!flashId) return undefined
		const t = setTimeout(() => setFlashId(null), 2200)
		return () => clearTimeout(t)
	}, [flashId])
	const highlight = useHighlight()

	// ── Mutations.
	function updateRule(id, patch) {
		setLimits(prev => prev.map(r => (r.id === id ? { ...r, ...patch } : r)))
	}
	/** The drawer saved through a direct call; the collection adopts the server's row. */
	function onSaved(saved) {
		sync.adopt(saved)
		setDrawer(null)
		setFlashId(saved.id)
		showToast.success('Rule saved')
	}
	function deleteRule(r) {
		showToast.confirm(readsAs(r, catalog.labels, matrixById.get(String(r.matrix_id))), {
			title: 'Delete this rule?', tone: 'danger', submitText: 'Delete rule',
			onSubmit: async () => {
				try {
					await setupApi.priceLimits.remove(r.id)
					sync.forget(r.id)
					showToast.success('Rule deleted')
				} catch (e) {
					showToast.error(describeApiError(e))
				}
			},
		})
	}
	// The import still goes through the collection (sequential POSTs by the
	// diff, criterion 20). The rows come back without `specificity` until a
	// reload, so one quiet reload follows the import's sync — armed only
	// here, not by any `saving` → false (an adopt's empty cycle must not).
	const importedRef = useRef(false)
	const wasSaving = useRef(false)
	useEffect(() => {
		if (wasSaving.current && !sync.saving && importedRef.current) {
			importedRef.current = false
			sync.reload({ quiet: true })
		}
		wasSaving.current = sync.saving
	}, [sync.saving])
	function importFromCsv(rows, mode) {
		setLimits(prev => (mode === 'replace' ? rows : [...rows, ...prev]))
		importedRef.current = true
		setPage(1); setCsvOpen(false)
		showToast.success(`${rows.length} rule${rows.length === 1 ? '' : 's'} imported`)
	}

	// ── Order, filters, sort, pages.
	const ctx = useMemo(() => ({ labels: catalog.labels, matrixById }), [catalog.labels, matrixById])
	const ordered = useMemo(() => engineOrder(limits), [limits])
	const filtered = useMemo(() => applyFilters(ordered, filters, ctx), [ordered, filters, ctx])
	const sorted = useMemo(() => (sort ? sortRows(filtered, sort, ctx) : filtered), [filtered, sort, ctx])
	const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE))
	// A cap in the Activity links here with `?highlight=<id>`; a Save flashes
	// its row; a verdict lights its first used rule. Any of them can be on
	// another page, so the page follows the row (criterion 8).
	// The newest gesture wins; the deep link only while its flash is on,
	// since the id stays in the URL and would otherwise pull the page back.
	useEffect(() => {
		const id = jumpId || flashId || (highlight.active ? highlight.id : null)
		if (!id) return
		const at = sorted.findIndex(r => String(r.id) === String(id))
		if (at >= 0) setPage(Math.floor(at / PAGE_SIZE) + 1)
		if (jumpId) setJumpId(null)
	}, [highlight.id, highlight.active, flashId, jumpId, sorted])
	const currentPage = Math.min(page, totalPages)
	const paged = sorted.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE)
	const counts = useMemo(() => countsByKind(limits), [limits])

	const uncoveredDays = useMemo(() => {
		if (!filters.date_from || !filters.date_to) return []
		const days = enumerateDays(filters.date_from, filters.date_to)
		if (!days.length) return []
		if (days.length > 366) return { tooBig: true, count: days.length }
		return days.filter(d => !filtered.some(r => ruleCoversDay(r, d)))
	}, [filters.date_from, filters.date_to, filtered])

	const activeFiltersCount = Object.keys(EMPTY_FILTERS).filter(k => filters[k] !== EMPTY_FILTERS[k]).length
	const setF = (patch) => { setFilters(f => ({ ...f, ...patch })); setPage(1); setVerdict(null) }
	const clearFilters = () => { setFilters(EMPTY_FILTERS); setPage(1); setVerdict(null) }
	const toggleSort = (key) => setSort(s => (!s || s.key !== key ? { key, dir: 'asc' } : s.dir === 'asc' ? { key, dir: 'desc' } : null))

	// The offer-class axes are data-driven: a select shows only when a rule uses the axis.
	const inUse = useMemo(() => ({
		franchise: [...new Set(limits.map(r => idKey(r.scope_ps_provider_id)).filter(Boolean))].sort(),
		tariff:    [...new Set(limits.map(r => prepaidKey(r.scope_is_prepaid)).filter(Boolean))].sort(),
		insurance: [...new Set(limits.map(r => idKey(r.scope_ps_insurance_id)).filter(Boolean))].sort(),
	}), [limits])

	const loading = sync.loading
	const loadError = !!sync.error && !limits.length && !sync.loading

	// ── Which rows the panel lit, and how (criterion 17).
	function onVerdict(v) {
		setVerdict(v)
		const first = v?.ordinary.find(o => o.used)?.id || [...(v?.usedIds || [])][0] || null
		if (first) setJumpId(first)
	}
	const litSx = (r) => {
		const id = String(r.id)
		if (!verdict || !verdict.litIds.has(id)) return null
		if (verdict.usedIds.has(id)) {
			const prot = !!r.protects_dates
			const bg = prot ? dsColorNew.noteInfoBg : dsColorNew.pillActiveBg
			return { bgcolor: bg, boxShadow: `inset 3px 0 0 ${prot ? dsColorNew.noteInfo : dsColorNew.appBlue}`, '&:hover': { bgcolor: bg } }
		}
		return { bgcolor: dsColorNew.tableRowAlt, boxShadow: `inset 3px 0 0 ${dsColorNew.grayBorder}`,
		         '& td, & td .MuiTypography-root, & td span': { color: dsColorNew.textSubtle } }
	}

	const subtitle = activeFiltersCount > 0
		? `${filtered.length} of ${counts.all} shown`
		: `${counts.all} rule${counts.all === 1 ? '' : 's'} · ${sort ? `sorted by ${SORT_LABEL[sort.key]} ${sort.dir === 'asc' ? '▲' : '▼'}` : 'sorted as the engine reads them'}`

	return (
		<Surface padded={false}>
			<Box sx={{ p: 2.5 }}>
				{/* ── Title, search, the three actions ── */}
				<Stack direction="row" alignItems="center" gap={1} flexWrap="wrap" sx={{ mb: 2 }}>
					<AppCardTitle icon="list-check" title="Price limits" subtitle={subtitle}>
						<AppInfoButton title="Price limits" sx={{ ml: 0.25 }}>
							<p><strong>Hard floors</strong> — and optional ceilings — no Strategy can pass, as shelf prices.</p>
							<p>Among ordinary rules <strong>the most specific wins</strong>. A <strong>date protection</strong> reaches any
							rental of up to 30 days that crosses its dates and sits on top of all of them: the floor of a cell is the
							higher of the two.</p>
							<p>Not sure what a cell gets? Press <strong>Test a cell</strong> — the engine answers.</p>
						</AppInfoButton>
					</AppCardTitle>
					<Box sx={{ flex: 1 }}/>
					<TextField size="small" placeholder="Search office, ACRISS, matrix, protection…"
					           value={filters.search}
					           onChange={(e) => setF({ search: e.target.value })}
					           onKeyDown={(e) => {
					               if (e.key === 'Escape') { setF({ search: '' }); e.currentTarget.blur() }
					               if (e.key === 'Enter') e.currentTarget.blur()
					           }}
					           InputProps={{
					               startAdornment: <SearchIcon sx={{ fontSize: 14, color: dsColorNew.textSubtle, mr: 1 }}/>,
					               endAdornment: filters.search ? (
					                   <IconButton size="small" onClick={() => setF({ search: '' })} aria-label="Clear search" sx={{ mr: -0.5 }}>
					                       <IconSeeker icon="xmark" sx={{ fontSize: 13, color: dsColorNew.textSubtle }}/>
					                   </IconButton>
					               ) : null,
					           }}
					           sx={{ width: 340, '& .MuiOutlinedInput-root': { height: 38, bgcolor: dsColorNew.filterInputBg } }}/>
					<IconButton title="Import CSV — DS format or a RateGain / Carwiz export, auto-detected" aria-label="Import CSV"
					            onClick={() => setCsvOpen(true)} sx={toolIconSx}>
						<UploadIcon sx={{ fontSize: 16 }}/>
					</IconButton>
					<IconButton title="Export CSV" aria-label="Export CSV" disabled={!limits.length}
					            onClick={() => downloadCsv(limits)} sx={toolIconSx}>
						<DownloadIcon sx={{ fontSize: 16 }}/>
					</IconButton>
					{/* "What applies here?" as a dialog: the secondary action beside the create, as Products / Tethering place theirs */}
					<Button variant="outlined" onClick={() => setExplainOpen(true)}
					        startIcon={<IconSeeker icon="magnifying-glass" sx={{ fontSize: 13 }}/>}
					        sx={{ ...secondaryButtonSx, height: 38, whiteSpace: 'nowrap', ml: 0.5 }}>
						Test a cell
					</Button>
					<AppToolbarCreateButton label="Add rule" icon="plus" onClick={() => setDrawer({ mode: 'new', initial: null })}
					                        sx={{ height: 38, textTransform: 'none' }}/>
				</Stack>

				{/* ── Filters (AppFilterBar) ── */}
				<AppFilterBar label="Filters" sx={{ mb: 1.5 }}
				              actions={activeFiltersCount > 0 && (
				              	<Button size="small" onClick={clearFilters}
				              	        sx={{ textTransform: 'none', fontWeight: 500, color: dsColorNew.appBlue }}>Clear</Button>
				              )}>
					<DateRangeField from={filters.date_from} to={filters.date_to}
					                placeholder="Active on… any dates" cleanable presets={futurePresets()} width={220}
					                onChange={({ from, to }) => setF({ date_from: from || '', date_to: to || '' })}/>
					<CompactSelect value={filters.office_scope} onChange={(v) => setF({ office_scope: v })} minWidth={160}
					               options={[{ value: '*', label: 'Any office' },
					                         ...catalog.groups.map(g => ({ value: `group:${g.code}`, label: `Group · ${g.name}` })),
					                         ...catalog.offices.map(o => ({ value: `office:${o.code}`, label: `Office · ${o.code}` }))]}/>
					<CompactSelect value={filters.category_scope} onChange={(v) => setF({ category_scope: v })} minWidth={160}
					               options={[{ value: '*', label: 'Any category' },
					                         ...catalog.tree.flatMap(m => [
					                         	{ value: `macro:${m.code}`, label: `Macro · ${m.name}` },
					                         	...m.sipps.map(s => ({ value: `sipp:${s}`, label: `   ${s}` })),
					                         ])]}/>
					<CompactSelect value={filters.duration_unit} onChange={(v) => setF({ duration_unit: v })} minWidth={160}
					               options={[{ value: '*', label: 'Any duration' },
					                         { value: 'null', label: 'Any length (no range)' },
					                         { value: 'days', label: 'Has a length range' }]}/>
					{inUse.franchise.length > 0 && (
						<CompactSelect value={filters.franchise_scope} onChange={(v) => setF({ franchise_scope: v })} minWidth={170}
						               options={[{ value: '*', label: 'Any franchise' },
						                         ...inUse.franchise.map(v => ({ value: `franchise:${v}`, label: `Franchise · ${catalog.axes.franchiseName(Number(v))}` }))]}/>
					)}
					{inUse.tariff.length > 0 && (
						<CompactSelect value={filters.tariff_scope} onChange={(v) => setF({ tariff_scope: v })} minWidth={170}
						               options={[{ value: '*', label: 'Any tariff type' },
						                         ...inUse.tariff.map(v => ({ value: `tariff:${v}`, label: `Tariff type · ${v === '1' ? 'Prepaid' : 'Non prepaid'}` }))]}/>
					)}
					{inUse.insurance.length > 0 && (
						<CompactSelect value={filters.insurance_scope} onChange={(v) => setF({ insurance_scope: v })} minWidth={170}
						               options={[{ value: '*', label: 'Any insurance' },
						                         ...inUse.insurance.map(v => ({ value: `insurance:${v}`, label: `Insurance · ${catalog.axes.insuranceName(Number(v))}` }))]}/>
					)}
				</AppFilterBar>

				{/* ── Kind facets with counts; the counters add up to the total ── */}
				<Stack direction="row" gap={1} alignItems="center" flexWrap="wrap" sx={{ mb: 1.5 }}>
					{KIND_CHIPS.map(k => (
						<AppFilterChip key={k.key} selected={filters.kind === k.key} onClick={() => setF({ kind: k.key })}
						               label={<>{k.label} <Box component="b" sx={{ fontWeight: 700, color: filters.kind === k.key ? dsColorNew.appBlue : dsColorNew.textMuted }}>{counts[k.key]}</Box></>}/>
					))}
					<Typography sx={{ ml: 'auto', fontSize: 12, color: dsColorNew.textSubtle }}>
						{sort ? (
							<>sorted by {SORT_LABEL[sort.key]} {sort.dir === 'asc' ? '▲' : '▼'} —{' '}
							<Link component="button" type="button" onClick={() => setSort(null)}
							      sx={{ fontSize: 'inherit', color: dsColorNew.appBlue, verticalAlign: 'baseline' }}>
								back to the engine's order
							</Link></>
						) : 'Sort by a column to leave the engine\'s order · click the header again to come back'}
					</Typography>
				</Stack>

				<CoverageGapWarning filters={filters} uncovered={uncoveredDays}/>

				{loading ? (
					<AppTableSkeleton rows={8} columns={5}/>
				) : loadError ? (
					<AppNote severity="error">
						<strong>Could not load the price limits</strong> — webback answered {sync.error?.status || 'with an error'}.{' '}
						<Link component="button" type="button" onClick={() => sync.reload()}
						      sx={{ color: 'inherit', fontWeight: 600, fontSize: 'inherit', verticalAlign: 'baseline' }}>
							Try again
						</Link>
						{' '}— nothing was changed.
					</AppNote>
				) : !limits.length && activeFiltersCount === 0 ? (
					<EmptyState
						title="No price limits yet"
						description={<>Add a floor for everything first, then narrow it by office, category or season.
						              {matrices.length > 0 && <> You have <strong>{matrices.length} matri{matrices.length === 1 ? 'x' : 'ces'}</strong> — a
						              matrix sets no floor until a rule here points at it.</>}</>}
						action={
							<Stack direction="row" gap={1} justifyContent="center">
								<AppToolbarCreateButton divider={false} label="Add rule" icon="plus" sx={{ textTransform: 'none' }}
								                        onClick={() => setDrawer({ mode: 'new', initial: null })}/>
								<Button variant="outlined" startIcon={<UploadIcon/>} onClick={() => setCsvOpen(true)}
								        sx={{ ...secondaryButtonSx, height: 36 }}>Import CSV</Button>
							</Stack>}/>
				) : sorted.length === 0 ? (
					<EmptyState
						title="No rules match these filters"
						description="Clear the filters, or press Test a cell to ask the engine about a specific cell."
						action={<Button variant="outlined" onClick={clearFilters} sx={{ ...secondaryButtonSx, height: 36 }}>Clear filters</Button>}/>
				) : (
					<>
						<TableContainer sx={{ border: `1px solid ${dsColorNew.grayBorder}`, borderRadius: '10px', overflow: 'hidden' }}>
							<Table size="small" sx={{ tableLayout: 'fixed' }}>
								<TableHead>
									<TableRow>
										<HeadCell sortable active={sort?.key === 'where'} dir={sort?.dir} onSort={() => toggleSort('where')}>Where</HeadCell>
										<HeadCell sortable active={sort?.key === 'when'} dir={sort?.dir} onSort={() => toggleSort('when')} sx={{ width: 300 }}>When</HeadCell>
										<HeadCell sortable active={sort?.key === 'floor'} dir={sort?.dir} onSort={() => toggleSort('floor')} align="right" sx={{ width: 220 }}>Floor €/day</HeadCell>
										<HeadCell sortable active={sort?.key === 'ceiling'} dir={sort?.dir} onSort={() => toggleSort('ceiling')} align="right" sx={{ width: 130 }}>Ceiling €/day</HeadCell>
										<HeadCell sx={{ width: 150 }}></HeadCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{paged.map(r => {
										const lit = (highlight.active && String(r.id) === String(highlight.id)) || flashId === r.id
										const matrix = matrixById.get(String(r.matrix_id))
										const refused = sync.refused?.get(r.id)
										// M9, option A: after a refusal the cell shows the server's
										// amount and the chip says what was typed.
										const serverRow = refused ? sync.serverRowOf(r.id) : null
										const floorShown = serverRow ? serverRow.min_price : r.min_price
										const ceilingShown = serverRow ? serverRow.max_price : r.max_price
										const typed = serverRow
											? (serverRow.min_price !== r.min_price ? r.min_price : serverRow.max_price !== r.max_price ? r.max_price : null)
											: null
										const refusedShown = refused && typed != null
											? { ...refused, message: `${refused.message} — you typed ${fmtMoney(typed)}` } : refused
										return (
											<TableRow key={r.id} ref={lit ? scrollIntoView : undefined}
											          sx={{ ...rowHoverSx, verticalAlign: 'top', ...(litSx(r) || {}), ...(highlightSx(lit) || {}) }}>
												<TableCell sx={cellSx}><WhereCell rule={r} labels={catalog.labels}/></TableCell>
												<TableCell sx={cellSx}><WhenCell rule={r}/></TableCell>
												<TableCell sx={cellSx} align="right">
													<FloorCell rule={r} matrix={matrix} value={floorShown} onCommit={(p) => updateRule(r.id, p)}/>
												</TableCell>
												<TableCell sx={cellSx} align="right">
													<CeilingCell rule={r} value={ceilingShown} onCommit={(p) => updateRule(r.id, p)}/>
												</TableCell>
												<TableCell sx={cellSx}>
													<Stack direction="row" gap={0.25} alignItems="center" justifyContent="flex-end">
														{/* Only the server's judgement (criterion 14): the clone / binding /
														    protection reasons are gone with the rows that carried them. */}
														<UnsavedChip refused={refusedShown} stalled={sync.stalled?.has(r.id)}/>
														<IconButton size="small" title="Edit scope and dates" aria-label="Edit scope and dates"
														            onClick={() => setDrawer({ mode: 'edit', initial: r })} sx={rowIconSx}>
															<EditIcon fontSize="small"/>
														</IconButton>
														<IconButton size="small" title="Duplicate this rule" aria-label="Duplicate this rule"
														            onClick={() => setDrawer({ mode: 'duplicate', initial: r })} sx={rowIconSx}>
															<CopyIcon fontSize="small"/>
														</IconButton>
														<IconButton size="small" title="Delete this rule" aria-label="Delete this rule"
														            onClick={() => deleteRule(r)}
														            sx={{ ...rowIconSx, '&:hover': { color: dsColorNew.dangerRed, bgcolor: dsColorNew.dangerBg } }}>
															<DeleteIcon fontSize="small"/>
														</IconButton>
													</Stack>
												</TableCell>
											</TableRow>
										)
									})}
								</TableBody>
							</Table>
						</TableContainer>

						<Stack direction="row" alignItems="center" justifyContent="space-between" gap={2} flexWrap="wrap" sx={{ mt: 1.5, px: 0.5 }}>
							<Stack direction="row" alignItems="center" gap={1.5} flexWrap="wrap">
								<Typography sx={{ fontSize: 12, color: dsColorNew.textMuted }}>
									Amounts save when you leave the field, like the rest of Setup. Scope and dates open the drawer.
								</Typography>
								<AppLegendChips variant="plain" groups={[
									[{ icon: 'file-arrow-up', label: 'Import CSV' }, { icon: 'download', label: 'Export CSV' }],
									[{ icon: 'pen', label: 'Edit scope and dates', short: 'Edit' },
									 { icon: 'clone', label: 'Duplicate this rule', short: 'Duplicate' },
									 { icon: 'trash-can', label: 'Delete this rule', short: 'Delete' }],
								]}/>
							</Stack>
							<Stack direction="row" gap={2} alignItems="center">
								<Typography sx={{ fontSize: 13, color: dsColorNew.textMuted }}>
									{(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, sorted.length)} of {sorted.length}
								</Typography>
								{sorted.length > PAGE_SIZE && (
									<Pagination count={totalPages} page={currentPage} onChange={(_, v) => setPage(v)} size="small" shape="rounded"/>
								)}
							</Stack>
						</Stack>
					</>
				)}

				<Box sx={{ mt: 2 }}>
					<HelpNote>
						A new or changed rule takes effect the next time the engine prices the cell — prices already in the PMS are not rewritten.
					</HelpNote>
				</Box>
			</Box>

			<RuleDrawer open={!!drawer} mode={drawer?.mode} initial={drawer?.initial}
			            rules={limits} matrices={matrices} catalog={catalog}
			            onSaved={onSaved} onClose={() => setDrawer(null)}
			            onOpenRule={(id) => { const r = limits.find(x => String(x.id) === String(id)); if (r) setDrawer({ mode: 'edit', initial: r }) }}/>
			<CsvImportDialog open={csvOpen} onClose={() => setCsvOpen(false)} onImport={importFromCsv}
			                 labels={catalog.labels} matrixById={matrixById} existing={limits.length}/>
			{/* Always mounted: the last cell and its verdict survive a close (reopening shows them). */}
			<ExplainDialog open={explainOpen} onClose={() => setExplainOpen(false)}
			               rules={limits} matrices={matrices} catalog={catalog} matrixById={matrixById} onVerdict={onVerdict}/>
		</Surface>
	)
}

/** Bring a linked-to row into view, once, as it mounts. */
const scrollIntoView = (el) => el?.scrollIntoView({ block: 'center', behavior: 'smooth' })

// The toolbar's icon actions: a 38 px outlined square like the header's, grey, app blue on hover
const toolIconSx = {
	width: 38, height: 38, p: 0, borderRadius: '8px', border: `1px solid ${dsColorNew.grayBorder}`,
	color: dsColorNew.textMuted, bgcolor: dsColorNew.white,
	'&:hover': { borderColor: dsColorNew.appBlue, color: dsColorNew.appBlue, bgcolor: dsColorNew.hoverBgLight },
}
const rowIconSx = {
	width: 30, height: 30, borderRadius: '8px', color: dsColorNew.textMuted,
	'&:hover': { color: dsColorNew.appBlue, bgcolor: dsColorNew.hoverBgLight },
}
